import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/ui/select';
import { ScrollArea } from '../../../components/ui/scroll-area';
import { FileXls, FilePdf, FileText } from '@phosphor-icons/react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * BinCardModal — shows a per-item Stock/Bin Card with running balance, filter by
 * warehouse + date range, plus PDF/Excel exports.
 */
export default function BinCardModal({ open, onClose, itemDescription }) {
  const [warehouses, setWarehouses] = useState([]);
  const [warehouseId, setWarehouseId] = useState(''); // '' = all
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchCard = useCallback(async () => {
    if (!itemDescription) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (warehouseId) params.set('warehouse_id', warehouseId);
      if (startDate) params.set('start_date', startDate);
      if (endDate) params.set('end_date', endDate);
      const url = `${API_URL}/inventory/items/${encodeURIComponent(itemDescription)}/bin-card${params.toString() ? '?' + params.toString() : ''}`;
      const res = await axios.get(url);
      setData(res.data);
    } catch (e) {
      toast.error('Failed to load bin card');
    } finally {
      setLoading(false);
    }
  }, [itemDescription, warehouseId, startDate, endDate]);

  useEffect(() => {
    if (!open) return;
    axios.get(`${API_URL}/inventory/warehouses`).then(r => setWarehouses(r.data)).catch(() => {});
    setWarehouseId('');
    setStartDate('');
    setEndDate('');
  }, [open]);

  useEffect(() => {
    if (open) fetchCard();
  }, [open, fetchCard]);

  const downloadFile = async (suffix, mime, ext) => {
    try {
      const params = new URLSearchParams();
      if (warehouseId) params.set('warehouse_id', warehouseId);
      if (startDate) params.set('start_date', startDate);
      if (endDate) params.set('end_date', endDate);
      const token = localStorage.getItem('token');
      const url = `${API_URL}/inventory/items/${encodeURIComponent(itemDescription)}/bin-card.${suffix}${params.toString() ? '?' + params.toString() : ''}`;
      const res = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(new Blob([blob], { type: mime }));
      a.download = `bin-card-${itemDescription.replace(/\s+/g, '_').slice(0, 40)}.${ext}`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      toast.error('Download failed');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="bin-card-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText size={20} weight="duotone" />
            Stock / Bin Card
            <span className="font-mono text-sm text-muted-foreground ml-2">— {itemDescription}</span>
          </DialogTitle>
          <DialogDescription>
            Per-item ledger with running balance. Filter by warehouse or date range, then export to PDF / Excel.
          </DialogDescription>
        </DialogHeader>

        {/* Filters */}
        <div className="grid grid-cols-4 gap-3 px-1 py-2 border-b">
          <div>
            <Label className="text-xs">Warehouse</Label>
            <Select value={warehouseId || 'all'} onValueChange={(v) => setWarehouseId(v === 'all' ? '' : v)}>
              <SelectTrigger data-testid="bc-warehouse"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All warehouses</SelectItem>
                {warehouses.map(w => <SelectItem key={w.id} value={w.id}>{w.code} · {w.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Start date</Label>
            <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} data-testid="bc-start" />
          </div>
          <div>
            <Label className="text-xs">End date</Label>
            <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} data-testid="bc-end" />
          </div>
          <div className="flex items-end gap-2">
            <Button size="sm" variant="outline" onClick={() => downloadFile('pdf', 'application/pdf', 'pdf')} data-testid="bc-export-pdf">
              <FilePdf size={14} className="mr-1" /> PDF
            </Button>
            <Button size="sm" variant="outline" onClick={() => downloadFile('xlsx', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'xlsx')} data-testid="bc-export-xlsx">
              <FileXls size={14} className="mr-1" /> Excel
            </Button>
          </div>
        </div>

        {/* Summary */}
        {data && (
          <div className="grid grid-cols-4 gap-2 my-2" data-testid="bc-summary">
            <div className="border rounded p-2 bg-slate-50 dark:bg-slate-800/40">
              <div className="text-[10px] uppercase text-muted-foreground tracking-wide">Opening</div>
              <div className="text-lg font-bold">{data.opening_balance?.toFixed(2)}</div>
            </div>
            <div className="border rounded p-2 bg-emerald-50 dark:bg-emerald-900/20">
              <div className="text-[10px] uppercase text-emerald-700 tracking-wide">Total IN</div>
              <div className="text-lg font-bold text-emerald-800 dark:text-emerald-300">{data.total_in?.toFixed(2)}</div>
            </div>
            <div className="border rounded p-2 bg-red-50 dark:bg-red-900/20">
              <div className="text-[10px] uppercase text-red-700 tracking-wide">Total OUT</div>
              <div className="text-lg font-bold text-red-800 dark:text-red-300">{data.total_out?.toFixed(2)}</div>
            </div>
            <div className="border rounded p-2 bg-amber-50 dark:bg-amber-900/20">
              <div className="text-[10px] uppercase text-amber-700 tracking-wide">Closing</div>
              <div className="text-lg font-bold text-amber-800 dark:text-amber-300">{data.closing_balance?.toFixed(2)}</div>
            </div>
          </div>
        )}

        <ScrollArea className="flex-1">
          {loading ? (
            <div className="py-12 text-center text-muted-foreground">Loading…</div>
          ) : !data || data.rows?.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground italic">No movements in the selected period.</div>
          ) : (
            <table className="w-full text-xs" data-testid="bc-table">
              <thead className="bg-muted/40 sticky top-0">
                <tr>
                  <th className="text-left px-2 py-1.5 font-semibold uppercase tracking-wide">Date</th>
                  <th className="text-left px-2 py-1.5 font-semibold uppercase tracking-wide">Reference</th>
                  <th className="text-left px-2 py-1.5 font-semibold uppercase tracking-wide">Warehouse</th>
                  <th className="text-left px-2 py-1.5 font-semibold uppercase tracking-wide">Bin</th>
                  <th className="text-right px-2 py-1.5 font-semibold uppercase tracking-wide bg-emerald-50">IN</th>
                  <th className="text-right px-2 py-1.5 font-semibold uppercase tracking-wide bg-red-50">OUT</th>
                  <th className="text-right px-2 py-1.5 font-semibold uppercase tracking-wide">Unit Cost</th>
                  <th className="text-right px-2 py-1.5 font-semibold uppercase tracking-wide bg-amber-50">Balance</th>
                  <th className="text-left px-2 py-1.5 font-semibold uppercase tracking-wide">Notes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {data.rows.map((r, idx) => (
                  <tr key={idx} className="hover:bg-muted/30">
                    <td className="px-2 py-1.5 whitespace-nowrap">{r.date}</td>
                    <td className="px-2 py-1.5 font-mono text-[11px]">
                      <span className="capitalize text-muted-foreground">{r.reference_type}</span>
                      {' · '}
                      <span className="font-semibold">{r.reference_number}</span>
                    </td>
                    <td className="px-2 py-1.5">{r.warehouse_name}</td>
                    <td className="px-2 py-1.5 text-muted-foreground">{r.bin_location || '—'}</td>
                    <td className="px-2 py-1.5 text-right font-mono tabular-nums text-emerald-700">{r.in_qty ? r.in_qty.toFixed(2) : ''}</td>
                    <td className="px-2 py-1.5 text-right font-mono tabular-nums text-red-700">{r.out_qty ? r.out_qty.toFixed(2) : ''}</td>
                    <td className="px-2 py-1.5 text-right font-mono tabular-nums text-muted-foreground">{(r.unit_cost || 0).toFixed(2)}</td>
                    <td className="px-2 py-1.5 text-right font-mono tabular-nums font-bold bg-amber-50/40">{r.balance.toFixed(2)}</td>
                    <td className="px-2 py-1.5 text-xs text-muted-foreground">{r.notes || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
